import React, { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { FileSpreadsheet, Upload } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ModalSuccess } from '../training/ModalSuccess';
import { trainingProducts } from '../../data/trainingProducts';
import { workerTrainingStore } from '../../services/workerTrainingStore';
import { courseExpiryDate, formatItDate } from '../../utils/workers';
import { AssignableWorker } from './AssignTrainingModal';
interface TrainingImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  workers: AssignableWorker[];
}
interface ImportRow {
  workerId: number;
  workerName: string;
  productCode: string;
  date: string;
}
const sampleProducts = ['FOR-FGL', 'FOR-FSM', 'FOR-PSB', 'FOR-AI2', 'FOR-PRE'];
const sampleDates = ['2024-02-12', '2023-11-06', '2025-03-18', '2022-09-27', '2025-01-20'];

// DIRETTA MASSIVA: file with the training of the workers (simulated upload)
export function TrainingImportModal({
  isOpen,
  onClose,
  workers
}: TrainingImportModalProps) {
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    setRows([]);
    setDone(false);
  }, [isOpen]);
  const handleFile = () => setRows(workers.slice(0, 5).map((w, i) => ({
    workerId: w.id,
    workerName: w.name,
    productCode: sampleProducts[i % sampleProducts.length],
    date: sampleDates[i % sampleDates.length]
  })));
  const confirm = () => {
    const stamp = Date.now();
    workerTrainingStore.addMany(rows.map((r, i) => ({
      workerId: r.workerId,
      course: {
        id: `imp-${stamp}-${i}`,
        name: productName(r.productCode),
        productCode: r.productCode,
        firstDate: r.date,
        updateDate: '',
        expiryDate: courseExpiryDate(r.productCode, r.date),
        certificateName: '',
        expiryAlert: true,
        source: 'massiva',
        done: true
      }
    })));
    setDone(true);
    setTimeout(onClose, 1400);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title="Import formazione lavoratori" size="lg" data-id="element-1909" data-mp="1djnjbqsij0af">
      <AnimatePresence mode="wait" data-id="element-1910" data-mp="19k4wuql1mh5h">
        {done ? <ModalSuccess title="Formazione importata" subtitle={`${rows.length} corsi registrati nelle schede dei lavoratori`} data-id="element-1911" data-mp="14ks820am3009" /> : rows.length === 0 ? <div className="space-y-5" data-id="element-1912" data-mp="1x9h4hm0q2h5o">
            <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-slate-200 rounded-xl p-10 text-center cursor-pointer hover:border-seform-orange/60 transition-colors" data-id="element-1913" data-mp="1xaipojbgck14">
              <FileSpreadsheet className="h-10 w-10 text-seform-orange" data-id="element-1914" data-mp="1nai7j0pbk9yi" />
              <span className="text-sm font-medium text-slate-800" data-id="element-1915" data-mp="gvza5yafii30">Carica il file CSV o Excel</span>
              <span className="text-xs text-slate-500" data-id="element-1916" data-mp="butfhux7kkqu">
                Colonne: Codice fiscale, Lavoratore, Codice prodotto, Data di erogazione
              </span>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-seform-orange" data-id="element-1917" data-mp="6qrcqy06t3rx">
                <Upload className="h-4 w-4" data-id="element-1918" data-mp="bqdmgxgt41vz" /> Scegli file
              </span>
              <input type="file" accept=".csv,.xlsx" className="sr-only" onChange={handleFile} data-id="element-1919" data-mp="bo9ads8r7wkm" />
            </label>
            <p className="text-xs text-slate-500" data-id="element-1920" data-mp="kinzemj36lgx">
              La data di scadenza viene calcolata dagli anni di validità di ogni corso.
            </p>
          </div> : <div className="space-y-5" data-id="element-1921" data-mp="pjbnazc0qm6x">
            <div className="border border-slate-100 rounded-xl overflow-x-auto" data-id="element-1922" data-mp="ugbsz94yhmxi">
              <table className="w-full text-sm text-left" data-id="element-1923" data-mp="zg821msig4jx">
                <thead className="bg-slate-50 text-slate-500 font-semibold" data-id="element-1924" data-mp="zdltbjtvvj04">
                  <tr data-id="element-1925" data-mp="2w9j92kse88kg">
                    <th className="p-3" data-id="element-1926" data-mp="2wa1hsgw8sf99">Lavoratore</th>
                    <th className="p-3" data-id="element-1927" data-mp="3182xqsydfygi">Corso</th>
                    <th className="p-3 whitespace-nowrap" data-id="element-1928" data-mp="318w6fzgw4y1a">Data di erogazione</th>
                    <th className="p-3 whitespace-nowrap" data-id="element-1929" data-mp="vgxz61t71nde">Scadenza</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-1930" data-mp="qm0pcso3b9zg">
                  {rows.map(r => <tr key={`${r.workerId}-${r.productCode}`} data-id="element-1931" data-mp="14h3485ax09oe">
                      <td className="p-3 font-medium text-slate-800" data-id="element-1932" data-mp="zkvxiaap2u5e">{r.workerName}</td>
                      <td className="p-3" data-id="element-1933" data-mp="bvl4jezl641t">
                        {productName(r.productCode)}
                        <span className="block text-xs font-mono text-slate-400" data-id="element-1934" data-mp="7u80nag20axj">{r.productCode}</span>
                      </td>
                      <td className="p-3 whitespace-nowrap" data-id="element-1935" data-mp="llwblaekgtcx">{formatItDate(r.date)}</td>
                      <td className="p-3 whitespace-nowrap" data-id="element-1936" data-mp="gsbls5n4n5ot">
                        {formatItDate(courseExpiryDate(r.productCode, r.date)) === '-' ? 'Nessuna' : formatItDate(courseExpiryDate(r.productCode, r.date))}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1937" data-mp="24ku7xv0btr2s">
              <Button variant="flat" onClick={() => setRows([])} data-id="element-1938" data-mp="1qt5sbhqvckh2">
                Cambia file
              </Button>
              <Button onClick={confirm} data-id="element-1939" data-mp="1vr7dq9brovvp">Importa {rows.length} corsi</Button>
            </div>
          </div>}
      </AnimatePresence>
    </Modal>;
}
function productName(code: string): string {
  return trainingProducts.find(p => p.code === code)?.name || code;
}